import {
  memo,
  useState,
  useCallback,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react'

import { clsx } from 'clsx'

import { Separator, Skeleton, SkeletonText, Flex } from '@buffer-mono/popcorn'

import type { FragmentType } from '~publish/gql'
import type { Channel, CommentData } from '~publish/gql/graphql'

import { CommentDeletedError } from '../CommentDeletedError'
import { CommentItem } from '../CommentItem'
import { CommentActions } from '../CommentItem/CommentActions'
import { CommentAvatar } from '../CommentItem/CommentAvatar'
import commentItemStyles from '../CommentItem/CommentItem.module.css'
import { LastAuthorReply } from '../CommentItem/LastAuthorReply'
import { CommentMedia } from '../CommentMedia'
import { CommentReplyForm as ReplyForm } from '../CommentReplyForm'
import type { FilterStatus } from '../CommentsFilters/types'
import { CommentHeader } from '../components/CommentHeader'
import { CommentText } from '../components/CommentText/CommentText'
import {
  CommentContextProvider,
  useCommentData,
} from '../context/CommentContext'
import type { CommentsPostCard_Post } from '../fragments/CommentsPostCard_Post'
import { PostMiniatureButton } from '../PostMiniature'

import styles from './CommentCard.module.css'

export const CommentCardSkeleton = (): JSX.Element => {
  return (
    <section className={styles.container}>
      <div className={styles.threadContent}>
        {/* Main comment skeleton using CommentItem structure */}
        <div className={clsx(commentItemStyles.card, commentItemStyles.root)}>
          <aside className={commentItemStyles.avatar}>
            <Skeleton rounded width={36} height={36} />
          </aside>
          <div className={commentItemStyles.content}>
            <header>
              {/* Header skeleton - matches Flex structure with justify="between" */}
              <Flex align="center" gap="xs" justify="between">
                <Flex align="center" gap="xs">
                  <SkeletonText lines={1} width="6ch" /> {/* Username */}
                  <SkeletonText lines={1} width="4ch" /> {/* Time */}
                </Flex>
                <Skeleton rounded width={24} height={24} />{' '}
                {/* Action button */}
              </Flex>
            </header>
            {/* Content skeleton */}
            <SkeletonText lines={3} width="100%" />
          </div>
        </div>
      </div>
      <Skeleton width="160px" height="75px" />
    </section>
  )
}

export const CommentWrapperSkeleton = (): JSX.Element => {
  return (
    <>
      <div className={styles.wrapper}>
        <CommentCardSkeleton />
      </div>
      <Separator className={styles.separator} />
    </>
  )
}

const CommentCardContainer = forwardRef<
  HTMLElement,
  HTMLAttributes<HTMLElement> & {
    as?: 'article' | 'section'
    isContextComment?: boolean
    children: ReactNode
  }
>(
  (
    {
      as: Component = 'section',
      className,
      isContextComment,
      children,
      ...props
    },
    ref,
  ): JSX.Element => {
    const comment = useCommentData()
    const hasParentComment = comment.parentId !== null

    const semanticLabel = isContextComment
      ? `Context comment from ${comment.author.username}`
      : `Comment thread from ${comment.author.username}`

    const statusText =
      comment.status === 'replied'
        ? 'replied to'
        : comment.status === 'dismissed'
        ? 'dismissed'
        : 'needs attention'

    return (
      <Component
        ref={ref}
        key={comment.id}
        className={clsx(
          styles.container,
          !isContextComment && !hasParentComment && styles.onlyComment,
          className,
        )}
        data-testid="comment-card"
        data-comment-id={comment.id}
        aria-label={`${semanticLabel} ${
          comment.isOwnReply ? '(your reply)' : ''
        } - ${statusText}`}
        role={Component === 'section' ? 'group' : undefined}
        data-status={comment.status}
        {...props}
      >
        {children}
      </Component>
    )
  },
)
CommentCardContainer.displayName = 'CommentCardContainer'

type CommentCardProps = {
  comment: CommentData
  channel: Channel
  currentStatusFilter?: FilterStatus
  isContextComment?: boolean
  setShouldAnimateOut: (shouldAnimateOut: boolean) => void
  isFirstRepliableComment?: boolean
  onExpandNextComment?: () => void
  onManualExpansion?: (commentId: string | null) => void
}

const CommentReplyForm = memo(ReplyForm)

type CommentCardContentProps = {
  channel: Channel
  currentStatusFilter?: FilterStatus
  isContextComment: boolean
  setShouldAnimateOut: (shouldAnimateOut: boolean) => void
  isFirstRepliableComment?: boolean
  onExpandNextComment?: () => void
  onManualExpansion?: (commentId: string | null) => void
}

const CommentCardContent = ({
  channel,
  currentStatusFilter,
  isContextComment,
  setShouldAnimateOut,
  isFirstRepliableComment,
  onExpandNextComment,
  onManualExpansion,
}: CommentCardContentProps): JSX.Element => {
  const comment = useCommentData()
  const [actionsLoading, setActionsLoading] = useState(false)

  const handleActionsLoadingChange = useCallback((loading: boolean) => {
    setActionsLoading(loading)
  }, [])

  const isLoading = actionsLoading
  const hasParentComment = comment.parentId !== null
  const showActions = !isContextComment
  const showReplyForm =
    !isContextComment &&
    !comment.isDeleted &&
    comment.status !== 'dismissed' &&
    comment.status !== 'replied'

  return (
    <>
      <div className={styles.threadContent}>
        <CommentItem
          variant={isContextComment ? 'context' : 'root'}
          aria-labelledby={`comment-header-${comment.id}`}
          avatar={
            <CommentAvatar
              hasParent={hasParentComment}
              avatar={
                comment.isOwnReply ? channel.avatar : comment.author.avatar
              }
              username={comment.author.username}
              showDot={comment.status === 'new'}
              /* show connector only when another card follows */
              showConnector={Boolean(
                isContextComment || comment.lastReply || showReplyForm,
              )}
            />
          }
        >
          <header>
            <Flex align="center" gap="xs" justify="between">
              <CommentHeader />
              {showActions && (
                <CommentActions
                  channel={channel}
                  currentStatusFilter={currentStatusFilter}
                  setShouldAnimateOut={setShouldAnimateOut}
                  onLoadingChange={handleActionsLoadingChange}
                  disabled={isLoading}
                />
              )}
            </Flex>
          </header>

          <CommentText />
          <CommentMedia />
          {comment.isDeleted && <CommentDeletedError />}
        </CommentItem>

        <LastAuthorReply channel={channel} reply={comment.lastReply} />

        {showReplyForm && (
          <CommentReplyForm
            id={`${comment.id}-reply`}
            channel={channel}
            currentStatusFilter={currentStatusFilter}
            onAnimateOut={setShouldAnimateOut}
            initialExpanded={isFirstRepliableComment ?? false}
            onExpandNextComment={onExpandNextComment}
            onManualExpansion={onManualExpansion}
          />
        )}
      </div>
      {/* Post miniature preview (right column) */}
      {!isContextComment && (
        <PostMiniatureButton
          post={
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            comment.post as unknown as FragmentType<
              typeof CommentsPostCard_Post
            >
          }
        />
      )}
    </>
  )
}

export const CommentCard = forwardRef<HTMLElement, CommentCardProps>(
  (
    {
      comment,
      channel,
      currentStatusFilter,
      isContextComment = false,
      setShouldAnimateOut,
      isFirstRepliableComment = false,
      onExpandNextComment,
      onManualExpansion,
    },
    ref,
  ): JSX.Element | null => {
    if (!comment) {
      return null
    }

    return (
      <CommentContextProvider comment={comment}>
        <CommentCardContainer
          key={comment.id}
          ref={ref}
          isContextComment={isContextComment}
        >
          <CommentCardContent
            channel={channel}
            currentStatusFilter={currentStatusFilter}
            isContextComment={isContextComment}
            setShouldAnimateOut={setShouldAnimateOut}
            isFirstRepliableComment={isFirstRepliableComment}
            onExpandNextComment={onExpandNextComment}
            onManualExpansion={onManualExpansion}
          />
        </CommentCardContainer>
      </CommentContextProvider>
    )
  },
)

CommentCard.displayName = 'CommentCard'
